import { List, Map } from 'immutable';
import React, { useEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
    SortableContainer,
    SortableElement,
    SortableHandle,
} from 'react-sortable-hoc';

import {
    fetchQueue,
    removeQueueMember,
    removeQueueWaveMapping,
    updateQueue,
} from 'flow/actions/pbx-actions';
import Loading from 'flow/components/ui-components/Loading';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import { routes } from 'flow/types/routes';
import { extractName } from 'flow/utils';
import { translateNumber } from 'flow/utils/browser-utils';
import { twMerge } from 'tailwind-merge';

const ParentPage = {
    overview: 'overview',
    queue: 'queue',
    settings: 'settings',
};

const DragHandle = SortableHandle(() => <Icon iconKey="dragHandle" />);

const PbxQueueMemberEdit = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const history = useHistory();
    const { queueKey } = useParams();

    const queueMembers = useAppSelector((state) =>
        selectQueueMembers(state, queueKey),
    );

    const queueWaves = useAppSelector((state) =>
        state.pbx.getIn(['queues', queueKey, 'queueWaves']),
    );

    /**
     * Constructs a list containing both queueWaveDTOs and their queueWaveMappingDTOs. This is needed to
     * display the queuewaves as dividers between the different groups of queuewavemappings while still
     * being able to move mappings between waves, since moving items between multiple SortableLists is not
     * supported.
     */
    const flattenQueueWaveList = (queueWaves) =>
        queueWaves.flatMap((queueWave) => {
            return List.of(queueWave).concat(
                queueWave.get('queueWaveMappingDTOs'),
            );
        });

    const [queueMembersList, setQueueMembersList] = useState(queueMembers);
    const [queueWavesList, setQueueWavesList] = useState(
        queueWaves ? flattenQueueWaveList(queueWaves) : null,
    );

    useEffect(() => {
        dispatch(fetchQueue(queueKey));
    }, [dispatch, queueKey]);

    useEffect(() => {
        const queueWavesList = queueWaves
            ? flattenQueueWaveList(queueWaves)
            : null;
        setQueueWavesList(queueWavesList);
    }, [queueWaves]);

    if (!queueMembers) {
        return <Loading />;
    }

    const currentParentPage = () => {
        if (
            location.pathname.startsWith(
                generatePath(routes['pbx.overview.queueKey.members'], {
                    queueKey: queueKey,
                }),
            )
        ) {
            return ParentPage.overview;
        }

        if (
            location.pathname.startsWith(
                generatePath(routes['pbx.queues.queueKey.settings.members'], {
                    queueKey: queueKey,
                }),
            )
        ) {
            return ParentPage.settings;
        }

        return ParentPage.queue;
    };

    const handleBack = () => {
        const backPage = {
            overview: routes['pbx.overview.queueKey.members'],
            queue: routes['pbx.queues.queueKey.members'],
            settings: routes['pbx.queues.queueKey.settings.members'],
        };

        history.push(
            generatePath(backPage[currentParentPage()], {
                queueKey,
            }),
        );
    };

    /**
     * Takes a flattened list of queuewaves and queuewavemappings and puts the queuewavemappings
     * located directly after a queuewave in that queuewave. Returns a list that contains only
     * queuewaves, with the correct mappings in them.
     */
    const unFlattenQueueWaveList = (flattenedList) => {
        const queueWaves = [];
        let queueWaveMappings = [];
        let currentQueueWave;
        for (let i = 0; i < flattenedList.size; i++) {
            const item = flattenedList.get(i);
            if (item.get('key').startsWith('queuewavemapping-')) {
                queueWaveMappings.push(item);
            } else {
                if (currentQueueWave) {
                    queueWaves.push(
                        currentQueueWave.set(
                            'queueWaveMappingDTOs',
                            List(queueWaveMappings),
                        ),
                    );
                    queueWaveMappings = [];
                }
                currentQueueWave = item;
            }
        }
        queueWaves.push(
            currentQueueWave.set(
                'queueWaveMappingDTOs',
                List(queueWaveMappings),
            ),
        );
        return List(queueWaves);
    };

    const handleRemove = (memberKey) => {
        setQueueMembersList(
            queueMembersList.filter(
                (member) => member.get('key') !== memberKey,
            ),
        );
        dispatch(removeQueueMember(queueKey, memberKey));
    };

    const handleRemoveMapping = (queueWaveMapping) => {
        setQueueWavesList(
            queueWavesList.filter(
                (queueWave) =>
                    queueWave.get('key') !== queueWaveMapping.get('key'),
            ),
        );
        dispatch(removeQueueWaveMapping(queueKey, queueWaveMapping));
    };

    const renderExtension = (entity) => {
        let isQueueWaveMapping;
        let queueMember;
        if (entity.get('key').startsWith('queuewavemapping-')) {
            isQueueWaveMapping = true;
            queueMember = entity.get('queueMember');
        } else {
            queueMember = entity;
        }

        const name =
            ownExtensionKey === queueMember.get('extensionKey')
                ? i18next.t('chat.you')
                : extractName(queueMember.get('contact', Map()));

        return (
            <li
                key={queueMember.get('key')}
                value={queueMember.get('key')}
                className="relative z-30 list-none"
            >
                <SettingRow
                    className="h-12"
                    cta={
                        <div className="flex items-center gap-3">
                            <IconButton
                                aria-label={i18next.t('Delete.Button.Text')}
                                onClick={() =>
                                    isQueueWaveMapping
                                        ? handleRemoveMapping(entity)
                                        : handleRemove(queueMember.get('key'))
                                }
                            >
                                <Icon iconKey="delete" />
                            </IconButton>

                            <div className="cursor-move rounded-sm p-1 hover:bg-gray-200">
                                <DragHandle className="rounded-sm text-gray-600" />
                            </div>
                        </div>
                    }
                >
                    <ContactAvatar
                        contact={queueMember.get('contact')}
                        fallBackBackgroundColorClass={twMerge(
                            'group-hover:bg-gray-300 bg-gray-200',
                        )}
                        size="small"
                    />

                    <div className="truncate text-gray-800">{name}</div>
                </SettingRow>
            </li>
        );
    };

    const renderQueueWave = (queueWave) => (
        <div key={queueWave.get('key')}>
            <div
                className={twMerge(
                    'flex items-center justify-between px-2 py-5',
                    queueWave.get('key') !== 'queuewave-0' ? 'mt-6' : null,
                )}
            >
                <h2 className="text-xl/5 font-bold">
                    {queueWave.get('key') === 'queuewave-0'
                        ? i18next.t(
                              'Queue.HandleMembers.DelayedHuntGroup.Default',
                          )
                        : i18next.t('Queue.HandleMembers.DelayedHuntGroup')}
                </h2>

                {queueWave.get('key') !== 'queuewave-0' ? (
                    <div className="text-sm">
                        {i18next.t(
                            'Queue.HandleMembers.DelayedHuntGroup.After',
                        )}
                        &nbsp;
                        <span className="font-bold">
                            {`${translateNumber(
                                (queueWave.get('delay') -
                                    (queueWave.get('delay') % 60)) /
                                    60,
                            )} min ${translateNumber(
                                queueWave.get('delay') % 60,
                            )} sec`}
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    );

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (queueWaves && !queueWaves.isEmpty()) {
            if (newIndex === 0) {
                newIndex = 1;
            }
            const itemToMove = queueWavesList.get(oldIndex);
            const newList = queueWavesList
                .delete(oldIndex)
                .insert(newIndex, itemToMove);

            setQueueWavesList(newList);

            dispatch(
                updateQueue(queueKey, {
                    queueWaves: unFlattenQueueWaveList(newList),
                    queueMembers,
                }),
            );
        } else {
            const itemToMove = queueMembersList.get(oldIndex);
            const newList = queueMembersList
                .delete(oldIndex)
                .insert(newIndex, itemToMove);

            setQueueMembersList(newList);
            dispatch(
                updateQueue(queueKey, {
                    queueMembers: newList,
                }),
            );
        }
    };

    const SortableItem = SortableElement(({ value }) =>
        value.get('key').startsWith('queuewave-')
            ? renderQueueWave(value)
            : renderExtension(value),
    );

    const SortableList = SortableContainer(({ items }) => (
        <ul>
            {items?.map((value, index) => (
                <SortableItem
                    key={`item-${index}`}
                    index={index}
                    value={value}
                />
            ))}
        </ul>
    ));

    return (
        <Page header="pbx.edit_members" onClickBack={handleBack}>
            <SortableList
                items={
                    !queueWaves?.isEmpty() ? queueWavesList : queueMembersList
                }
                onSortEnd={onSortEnd}
                useDragHandle={true}
            />
        </Page>
    );
};

export default PbxQueueMemberEdit;
